import {
  Component,
  DestroyRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { externalServiceMap } from 'src/app/core/services/user.service';
import {
  ExternalServiceAccount,
  UserApiService,
} from 'src/app/api/services/user-api.service';

import { ExternalAuthService } from '@services/external-auth.service';
import { ExternalService, User } from 'src/app/core/models/core.models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, filter, switchMap, take, tap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { LoggerService } from '@services/logger.service';
import { selectUser } from 'src/app/core/reducers';
import { AppState } from 'src/app/reducers';
import { Store } from '@ngrx/store';
import {
  DialogCloseOptions,
  DialogCloseReason,
  DialogService,
} from '@sui/dialog';
import { ConfirmDialogComponent } from 'src/app/core/components/popups/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-user-linked-accounts',
  templateUrl: './user-linked-accounts.component.html',
  styleUrl: './user-linked-accounts.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class UserLinkedAccountsComponent implements OnInit, OnChanges {
  @Input() userId: string;

  readonly externalServiceMap = externalServiceMap;
  readonly accounts$ = new BehaviorSubject<ExternalServiceAccount[]>([]);

  private destroyRef = inject(DestroyRef);
  private translateService = inject(TranslateService);
  private loggerService = inject(LoggerService);
  private dialogService = inject(DialogService);
  private userId$ = new BehaviorSubject<string | null>(null);

  constructor(
    protected store: Store<AppState>,
    protected userApi: UserApiService,
    protected externalAuth: ExternalAuthService,
  ) {}

  ngOnInit() {
    this.userId$
      .pipe(
        switchMap((userId) =>
          userId ? this.userApi.getUserExternalAccounts(userId) : [],
        ),
        tap((accounts) => this.accounts$.next(accounts)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.externalAuth.registerCallback(
      ExternalService.MATTERMOST_MOE,
      (data) => {
        if (data?.external_service_id) {
          const updatedAccounts = this.accounts$.value.map((account) =>
            account.id === data.external_service_id
              ? { ...account, is_active: true }
              : account,
          );
          this.accounts$.next(updatedAccounts);
        }
      },
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['userId']?.currentValue) {
      this.userId$.next(this.userId);
    }
  }

  activate(service: ExternalService): void {
    this.store
      .select(selectUser)
      .pipe(
        // only proceed if we actually have a user object
        filter((user): user is User => !!user),
        // grab one value then auto-unsubscribe
        take(1),
        // perform the side-effect
        tap((user) => this.externalAuth.authenticate(service, user.id)),
      )
      .subscribe();
  }

  unlink(service: ExternalService) {
    const title = this.translateService.instant(
      'user.confirm-message.unlink-account',
    );
    const message = this.translateService.instant(
      'user.confirm-message.are-you-sure-you-want-to-unlink-this-account',
    );

    const dialogRef = this.dialogService.openSimpleDialog(
      ConfirmDialogComponent,
      {
        data: {
          title,
          message,
        },
        closeOnClick: false,
      },
    );

    dialogRef.closed.subscribe(
      (event: DialogCloseOptions<{ reason: DialogCloseReason }>) => {
        if (event.reason === DialogCloseReason.Confirm) {
          this.userApi
            .unlinkExternalAccount(this.userId, service)
            .pipe(
              tap(() => {
                const updatedAccounts = this.accounts$.value.map((account) =>
                  account.id === service
                    ? { ...account, is_active: false }
                    : account,
                );
                this.accounts$.next(updatedAccounts);
              }),
              takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();
        } else if (event.reason === DialogCloseReason.Cancel) {
          this.loggerService.debug(
            'User dismissed the modal without confirming the unlink action.',
            event.reason,
          );
        }
      },
    );
  }
}
